"use client";

/**
 * RealMathUniverse React Wrapper
 *
 * Wraps the Three.js simulation engine in a React component
 * for use in Next.js applications.
 */

import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

// Types
interface Particle {
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  acceleration: THREE.Vector3;
  speciesId: number;
  familyId: number;
  baseMass: number;
  effectiveMass: number;
  charge: number;
  color: THREE.Color;
  lifetime: number;
  probabilityWeight: number;
  speedMultiplier: number;
  turbulenceResponse: number;
  cohesionResponse: number;
  fieldResponse: {
    curvature: number;
    higgs: number;
    probability: number;
    em: number;
    strong: number;
    weak: number;
  };
}

interface Species {
  id: number;
  name: string;
  family: string;
  baseMass: number;
  charge: number;
  color: THREE.Color;
  speedMultiplier: number;
  turbulenceResponse: number;
  cohesionResponse: number;
  probabilityResponse: number;
  fieldCouplings: {
    curvature: number;
    higgs: number;
    probability: number;
    em: number;
    strong: number;
    weak: number;
  };
}

// 22-species registry
const SPECIES_REGISTRY: Species[] = [
  {
    id: 1,
    name: "crab_default",
    family: "data",
    baseMass: 1.0,
    charge: 0,
    color: new THREE.Color(0x66ccff),
    speedMultiplier: 1.0,
    turbulenceResponse: 0.5,
    cohesionResponse: 0.5,
    probabilityResponse: 0.5,
    fieldCouplings: {
      curvature: 0.5,
      higgs: 0.5,
      probability: 0.5,
      em: 0.0,
      strong: 0.0,
      weak: 0.0,
    },
  },
  {
    id: 2,
    name: "electron",
    family: "lepton",
    baseMass: 0.000511,
    charge: -1,
    color: new THREE.Color(0x00aaff),
    speedMultiplier: 2.0,
    turbulenceResponse: 0.8,
    cohesionResponse: 0.3,
    probabilityResponse: 0.9,
    fieldCouplings: {
      curvature: 0.1,
      higgs: 0.001,
      probability: 0.8,
      em: 1.0,
      strong: 0.0,
      weak: 0.3,
    },
  },
  {
    id: 3,
    name: "positron",
    family: "lepton",
    baseMass: 0.000511,
    charge: 1,
    color: new THREE.Color(0xff6600),
    speedMultiplier: 2.0,
    turbulenceResponse: 0.8,
    cohesionResponse: 0.3,
    probabilityResponse: 0.9,
    fieldCouplings: {
      curvature: 0.1,
      higgs: 0.001,
      probability: 0.8,
      em: 1.0,
      strong: 0.0,
      weak: 0.3,
    },
  },
  {
    id: 4,
    name: "electron_neutrino",
    family: "lepton",
    baseMass: 0.0000001,
    charge: 0,
    color: new THREE.Color(0xaaddff),
    speedMultiplier: 3.0,
    turbulenceResponse: 0.1,
    cohesionResponse: 0.0,
    probabilityResponse: 0.95,
    fieldCouplings: {
      curvature: 0.01,
      higgs: 0.0,
      probability: 0.9,
      em: 0.0,
      strong: 0.0,
      weak: 0.5,
    },
  },
  {
    id: 5,
    name: "up_quark",
    family: "quark",
    baseMass: 0.0022,
    charge: 0.667,
    color: new THREE.Color(0xff0000),
    speedMultiplier: 1.5,
    turbulenceResponse: 0.6,
    cohesionResponse: 0.8,
    probabilityResponse: 0.7,
    fieldCouplings: {
      curvature: 0.3,
      higgs: 0.01,
      probability: 0.6,
      em: 0.667,
      strong: 1.0,
      weak: 0.3,
    },
  },
  {
    id: 6,
    name: "down_quark",
    family: "quark",
    baseMass: 0.0047,
    charge: -0.333,
    color: new THREE.Color(0x00ff00),
    speedMultiplier: 1.5,
    turbulenceResponse: 0.6,
    cohesionResponse: 0.8,
    probabilityResponse: 0.7,
    fieldCouplings: {
      curvature: 0.3,
      higgs: 0.02,
      probability: 0.6,
      em: 0.333,
      strong: 1.0,
      weak: 0.3,
    },
  },
  {
    id: 7,
    name: "photon_like",
    family: "boson",
    baseMass: 0,
    charge: 0,
    color: new THREE.Color(0xffff00),
    speedMultiplier: 5.0,
    turbulenceResponse: 0.0,
    cohesionResponse: 0.0,
    probabilityResponse: 0.5,
    fieldCouplings: {
      curvature: 0.0,
      higgs: 0.0,
      probability: 0.3,
      em: 0.0,
      strong: 0.0,
      weak: 0.0,
    },
  },
  {
    id: 8,
    name: "gluon_like",
    family: "boson",
    baseMass: 0,
    charge: 0,
    color: new THREE.Color(0xff00ff),
    speedMultiplier: 4.0,
    turbulenceResponse: 0.2,
    cohesionResponse: 0.9,
    probabilityResponse: 0.4,
    fieldCouplings: {
      curvature: 0.0,
      higgs: 0.0,
      probability: 0.2,
      em: 0.0,
      strong: 1.0,
      weak: 0.0,
    },
  },
  {
    id: 9,
    name: "higgs_excitation",
    family: "boson",
    baseMass: 125.0,
    charge: 0,
    color: new THREE.Color(0xffffff),
    speedMultiplier: 0.3,
    turbulenceResponse: 0.1,
    cohesionResponse: 0.2,
    probabilityResponse: 0.3,
    fieldCouplings: {
      curvature: 0.8,
      higgs: 1.0,
      probability: 0.5,
      em: 0.0,
      strong: 0.0,
      weak: 0.5,
    },
  },
  {
    id: 10,
    name: "proton_like",
    family: "hadron",
    baseMass: 0.938,
    charge: 1,
    color: new THREE.Color(0xff4444),
    speedMultiplier: 0.8,
    turbulenceResponse: 0.4,
    cohesionResponse: 0.9,
    probabilityResponse: 0.5,
    fieldCouplings: {
      curvature: 0.7,
      higgs: 0.5,
      probability: 0.4,
      em: 1.0,
      strong: 0.9,
      weak: 0.2,
    },
  },
  {
    id: 11,
    name: "neutron_like",
    family: "hadron",
    baseMass: 0.94,
    charge: 0,
    color: new THREE.Color(0x888888),
    speedMultiplier: 0.8,
    turbulenceResponse: 0.4,
    cohesionResponse: 0.9,
    probabilityResponse: 0.5,
    fieldCouplings: {
      curvature: 0.7,
      higgs: 0.5,
      probability: 0.4,
      em: 0.0,
      strong: 0.9,
      weak: 0.2,
    },
  },
  {
    id: 12,
    name: "muon",
    family: "lepton",
    baseMass: 0.106,
    charge: -1,
    color: new THREE.Color(0x0088ff),
    speedMultiplier: 1.8,
    turbulenceResponse: 0.7,
    cohesionResponse: 0.3,
    probabilityResponse: 0.85,
    fieldCouplings: {
      curvature: 0.15,
      higgs: 0.05,
      probability: 0.75,
      em: 1.0,
      strong: 0.0,
      weak: 0.4,
    },
  },
  {
    id: 13,
    name: "tau",
    family: "lepton",
    baseMass: 1.777,
    charge: -1,
    color: new THREE.Color(0x004488),
    speedMultiplier: 1.2,
    turbulenceResponse: 0.5,
    cohesionResponse: 0.4,
    probabilityResponse: 0.7,
    fieldCouplings: {
      curvature: 0.4,
      higgs: 0.3,
      probability: 0.6,
      em: 1.0,
      strong: 0.0,
      weak: 0.5,
    },
  },
  {
    id: 14,
    name: "muon_neutrino",
    family: "lepton",
    baseMass: 0.0000002,
    charge: 0,
    color: new THREE.Color(0x88bbff),
    speedMultiplier: 3.0,
    turbulenceResponse: 0.1,
    cohesionResponse: 0.0,
    probabilityResponse: 0.95,
    fieldCouplings: {
      curvature: 0.01,
      higgs: 0.0,
      probability: 0.9,
      em: 0.0,
      strong: 0.0,
      weak: 0.5,
    },
  },
  {
    id: 15,
    name: "tau_neutrino",
    family: "lepton",
    baseMass: 0.0000003,
    charge: 0,
    color: new THREE.Color(0x6699ff),
    speedMultiplier: 3.0,
    turbulenceResponse: 0.1,
    cohesionResponse: 0.0,
    probabilityResponse: 0.95,
    fieldCouplings: {
      curvature: 0.01,
      higgs: 0.0,
      probability: 0.9,
      em: 0.0,
      strong: 0.0,
      weak: 0.5,
    },
  },
  {
    id: 16,
    name: "strange_quark",
    family: "quark",
    baseMass: 0.095,
    charge: -0.333,
    color: new THREE.Color(0x00cc00),
    speedMultiplier: 1.3,
    turbulenceResponse: 0.5,
    cohesionResponse: 0.75,
    probabilityResponse: 0.65,
    fieldCouplings: {
      curvature: 0.35,
      higgs: 0.1,
      probability: 0.55,
      em: 0.333,
      strong: 1.0,
      weak: 0.35,
    },
  },
  {
    id: 17,
    name: "charm_quark",
    family: "quark",
    baseMass: 1.27,
    charge: 0.667,
    color: new THREE.Color(0xcc0000),
    speedMultiplier: 1.0,
    turbulenceResponse: 0.4,
    cohesionResponse: 0.7,
    probabilityResponse: 0.55,
    fieldCouplings: {
      curvature: 0.5,
      higgs: 0.4,
      probability: 0.45,
      em: 0.667,
      strong: 1.0,
      weak: 0.4,
    },
  },
  {
    id: 18,
    name: "top_quark",
    family: "quark",
    baseMass: 173.0,
    charge: 0.667,
    color: new THREE.Color(0x880000),
    speedMultiplier: 0.3,
    turbulenceResponse: 0.2,
    cohesionResponse: 0.5,
    probabilityResponse: 0.3,
    fieldCouplings: {
      curvature: 0.9,
      higgs: 1.0,
      probability: 0.2,
      em: 0.667,
      strong: 1.0,
      weak: 0.5,
    },
  },
  {
    id: 19,
    name: "bottom_quark",
    family: "quark",
    baseMass: 4.18,
    charge: -0.333,
    color: new THREE.Color(0x008800),
    speedMultiplier: 0.7,
    turbulenceResponse: 0.35,
    cohesionResponse: 0.65,
    probabilityResponse: 0.45,
    fieldCouplings: {
      curvature: 0.6,
      higgs: 0.5,
      probability: 0.35,
      em: 0.333,
      strong: 1.0,
      weak: 0.45,
    },
  },
  {
    id: 20,
    name: "W_like",
    family: "boson",
    baseMass: 80.4,
    charge: 1,
    color: new THREE.Color(0xff8800),
    speedMultiplier: 0.5,
    turbulenceResponse: 0.15,
    cohesionResponse: 0.3,
    probabilityResponse: 0.4,
    fieldCouplings: {
      curvature: 0.6,
      higgs: 0.8,
      probability: 0.4,
      em: 0.5,
      strong: 0.0,
      weak: 1.0,
    },
  },
  {
    id: 21,
    name: "Z_like",
    family: "boson",
    baseMass: 91.2,
    charge: 0,
    color: new THREE.Color(0x8888ff),
    speedMultiplier: 0.4,
    turbulenceResponse: 0.1,
    cohesionResponse: 0.25,
    probabilityResponse: 0.35,
    fieldCouplings: {
      curvature: 0.65,
      higgs: 0.85,
      probability: 0.35,
      em: 0.0,
      strong: 0.0,
      weak: 1.0,
    },
  },
  {
    id: 22,
    name: "meson_like",
    family: "hadron",
    baseMass: 0.135,
    charge: 0,
    color: new THREE.Color(0xaaaa00),
    speedMultiplier: 1.1,
    turbulenceResponse: 0.5,
    cohesionResponse: 0.7,
    probabilityResponse: 0.55,
    fieldCouplings: {
      curvature: 0.4,
      higgs: 0.2,
      probability: 0.5,
      em: 0.0,
      strong: 0.8,
      weak: 0.15,
    },
  },
];

// Behavior modes
enum BehaviorMode {
  NONE = 0,
  STABLE_ORBIT = 1,
  BLACK_HOLE = 2,
  GEOSPATIAL_STATIC = 3,
  GEOSPATIAL_LIVE = 4,
  EXPANSION = 5,
  TURBULENCE_BURST = 6,
  COLLAPSE = 7,
}

const BEHAVIOR_NAMES = [
  "none/manual",
  "stable_orbit",
  "black_hole",
  "geospatial_static",
  "geospatial_live",
  "expansion",
  "turbulence_burst",
  "collapse",
];

const DEFAULT_PARTICLE_COUNT = 12000;
const MIN_PARTICLE_COUNT = 1000;
const MAX_PARTICLE_COUNT = 100000;
const HUD_UPDATE_INTERVAL_MS = 250;
const DEFAULT_TIME_SCALE = 0.22;
const MIN_TIME_SCALE = 0.03;
const MAX_TIME_SCALE = 1.0;
const MODULATION_SMOOTHING_RATE = 1.35;
const GRAVITY_SMOOTHING_RATE = 0.9;

function clampNumber(value: number, min: number, max: number) {
  if (!Number.isFinite(value)) return min;
  return Math.max(min, Math.min(max, value));
}

function getInitialTimeScale() {
  const fromQuery =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("speed")
      : null;
  const fromEnv = import.meta.env.VITE_RMU_TIME_SCALE;
  const raw = Number(fromQuery ?? fromEnv ?? DEFAULT_TIME_SCALE);
  return clampNumber(raw, MIN_TIME_SCALE, MAX_TIME_SCALE);
}

function getInitialParticleCount() {
  const fromQuery =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("particles")
      : null;
  const fromEnv = import.meta.env.VITE_RMU_PARTICLE_COUNT;
  const raw = Number(fromQuery ?? fromEnv ?? DEFAULT_PARTICLE_COUNT);
  return Math.floor(clampNumber(raw, MIN_PARTICLE_COUNT, MAX_PARTICLE_COUNT));
}

function getInitialVCVLoopScale() {
  const fromQuery =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("vcvScale")
      : null;
  const fromEnv = import.meta.env.VITE_RMU_VCV_LOOP_SCALE;
  const raw = Number(fromQuery ?? fromEnv ?? 6.0);
  return clampNumber(raw, 1.0, 30.0);
}


function getRmuSimulatorBasePath(): string {
  const viteBase = typeof import.meta !== "undefined" && import.meta.env?.BASE_URL
    ? String(import.meta.env.BASE_URL)
    : "/rmu-simulator/";

  // Force the deployed website to look inside /rmu-simulator/data even if the
  // visitor lands on /rmu-simulator without a trailing slash. Relative URLs like
  // data/file.csv can accidentally resolve to /data/file.csv on some hosts.
  const normalized = viteBase && viteBase !== "/" ? viteBase : "/rmu-simulator/";
  return normalized.endsWith("/") ? normalized : `${normalized}/`;
}

function getRmuDataUrl(fileName: string): string {
  const cleanName = fileName.replace(/^\/+/, "");
  if (cleanName.startsWith("http://") || cleanName.startsWith("https://")) {
    return cleanName;
  }
  if (cleanName.startsWith("rmu-simulator/data/")) {
    return `/${cleanName}`;
  }
  if (cleanName.startsWith("data/")) {
    return `${getRmuSimulatorBasePath()}${cleanName}`;
  }
  return `${getRmuSimulatorBasePath()}data/${cleanName}`;
}

function getRmuDataUrlCandidates(fileName: string): string[] {
  const cleanName = fileName.replace(/^\/+/, "");

  if (cleanName.startsWith("http://") || cleanName.startsWith("https://")) {
    return [cleanName];
  }

  const bareName = cleanName.split("/").pop() || cleanName;
  const primary = getRmuDataUrl(cleanName);

  // The first path is the intended FileZilla deployment path. The fallback paths
  // cover hosts that normalize /rmu-simulator differently or when testing locally.
  const candidates = [
    primary,
    `/rmu-simulator/data/${bareName}`,
    `${getRmuSimulatorBasePath()}data/${bareName}`,
    `data/${bareName}`,
    `/data/${bareName}`,
  ];

  return Array.from(new Set(candidates));
}

async function fetchFirstAvailableText(urls: string[]): Promise<{ text: string; url: string }> {
  const attempted: string[] = [];

  for (const url of urls) {
    const cacheBust = url.includes("?") ? `&rmu_v=${Date.now()}` : `?rmu_v=${Date.now()}`;
    const urlVariants = [url, `${url}${cacheBust}`];

    for (const finalUrl of urlVariants) {
      attempted.push(finalUrl);

      try {
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 8000);
        const response = await fetch(finalUrl, {
          cache: finalUrl === url ? "force-cache" : "no-store",
          signal: controller.signal,
        });
        window.clearTimeout(timeout);

        if (!response.ok) {
          console.log(`[RMU] Data file candidate failed ${response.status}: ${finalUrl}`);
          continue;
        }

        const text = await response.text();
        if (text.trim().length === 0) {
          console.log(`[RMU] Data file candidate was empty: ${finalUrl}`);
          continue;
        }

        console.log(`[RMU] Data file loaded from: ${finalUrl}`);
        return { text, url: finalUrl };
      } catch (error: any) {
        console.log(`[RMU] Data file candidate error: ${finalUrl}`, error?.message ?? error);
      }
    }
  }

  throw new Error(`No readable RMU data file. Tried: ${attempted.join(", ")}`);
}

function parseCSVLine(line: string): string[] {
  const values: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const next = line[i + 1];

    if (char === '"' && inQuotes && next === '"') {
      current += '"';
      i++;
      continue;
    }

    if (char === '"') {
      inQuotes = !inQuotes;
      continue;
    }

    if (char === "," && !inQuotes) {
      values.push(current);
      current = "";
      continue;
    }

    current += char;
  }

  values.push(current);
  return values;
}

function parseVCVCaptureCSV(csvText: string): VCVLoopEvent[] {
  const rawLines = csvText.split(/\r?\n/).filter(Boolean);
  if (rawLines.length < 2) return [];

  const headers = rawLines[0]
    .split(",")
    .map((h) => h.trim().replace(/^"|"$/g, "").toLowerCase());

  const idx = (name: string) => headers.indexOf(name);
  const elapsedIdx = idx("elapsed_ms");
  const addressIdx = idx("address");
  const channelIdx = idx("channel");
  const valueIdx = idx("value");
  const valueIndexIdx = idx("value_index");

  if (elapsedIdx < 0 || addressIdx < 0 || valueIdx < 0) return [];

  const grouped = new Map<string, VCVLoopEvent>();
  const maxEvents =
    typeof window !== "undefined"
      ? clampNumber(Number(new URLSearchParams(window.location.search).get("vcvmax") ?? 24000), 1000, 100000)
      : 24000;

  // Fast parser: the recorder CSV has raw_json as the final column. That field can be
  // very large and contains commas, so this intentionally reads only the first eight
  // columns and ignores raw_json for public playback.
  for (let lineIndex = 1; lineIndex < rawLines.length; lineIndex++) {
    const line = rawLines[lineIndex].trim();
    if (!line) continue;

    const cells = line.split(",", 8);
    const t = Number(cells[elapsedIdx]);
    const address = String(cells[addressIdx] ?? "").replace(/^"|"$/g, "").trim();
    const value = Number(String(cells[valueIdx] ?? "").replace(/^"|"$/g, ""));
    const ch = channelIdx >= 0 ? Number(String(cells[channelIdx] ?? "").replace(/^"|"$/g, "")) : undefined;
    const valueIndex = valueIndexIdx >= 0 ? Number(String(cells[valueIndexIdx] ?? "0").replace(/^"|"$/g, "")) : 0;

    if (!Number.isFinite(t) || !address || !Number.isFinite(value)) continue;

    const roundedT = Math.round(t);
    const key = `${roundedT}|${address}`;
    const existing = grouped.get(key) ?? {
      t: roundedT,
      address,
      args: [],
      ch: Number.isFinite(ch as number) ? ch : undefined,
    };

    const safeIndex = Number.isFinite(valueIndex) && valueIndex >= 0 ? Math.floor(valueIndex) : existing.args.length;
    existing.args[safeIndex] = value;
    existing.value = existing.args.length === 1 ? existing.args[0] : existing.value;
    existing.values = existing.args.length > 1 ? existing.args : existing.values;
    grouped.set(key, existing);

    if (grouped.size >= maxEvents) break;
  }

  return Array.from(grouped.values())
    .map((evt) => ({
      ...evt,
      args: evt.args.filter((value) => Number.isFinite(value)),
    }))
    .filter((evt) => evt.address && evt.args.length > 0 && Number.isFinite(evt.t))
    .sort((a, b) => a.t - b.t);
}

function parseVCVCaptureJSON(jsonText: string): { events: VCVLoopEvent[]; durationMs?: number; playbackScaleRecommended?: number } {
  const database = JSON.parse(jsonText);

  // v14 fast format: compact frame table. This is much smaller and quicker to
  // parse over shared hosting than the event JSON or the huge raw recorder CSV.
  // Shape:
  //   { format: "rmu_vcv_frame_loop_v2", frames: [{ t: 50, c: { "1": [..], "2": [..] } }] }
  if (Array.isArray(database.frames)) {
    const events: VCVLoopEvent[] = [];

    for (const frame of database.frames) {
      const t = Number(frame.t ?? 0);
      const channels = frame.c ?? frame.channels ?? {};
      if (!Number.isFinite(t) || !channels || typeof channels !== "object") continue;

      for (const [channelKey, rawValues] of Object.entries(channels)) {
        const ch = Number(channelKey);
        if (!Number.isFinite(ch)) continue;

        const args = Array.isArray(rawValues)
          ? rawValues.map(Number).filter(Number.isFinite)
          : [Number(rawValues)].filter(Number.isFinite);

        if (args.length === 0) continue;

        events.push({
          t,
          ch,
          address: `/ch/${ch}`,
          args,
          value: args[0],
          values: args.length > 1 ? args : undefined,
        });
      }
    }

    return {
      events: events.sort((a, b) => a.t - b.t),
      durationMs: Number(database.durationMs ?? database.metadata?.durationMs),
      playbackScaleRecommended: Number(database.playbackScaleRecommended),
    };
  }

  // v12/v13 compact event format fallback.
  const rawEvents = Array.isArray(database.events) ? database.events : [];
  const events: VCVLoopEvent[] = rawEvents
    .map((evt: any) => ({
      t: Number(evt.t ?? evt.timeMs ?? 0),
      address: String(evt.address ?? ""),
      args: Array.isArray(evt.args) ? evt.args.map(Number).filter(Number.isFinite) : [],
      ch: evt.ch !== undefined ? Number(evt.ch) : undefined,
      value: evt.value !== undefined ? Number(evt.value) : undefined,
      values: Array.isArray(evt.values) ? evt.values.map(Number).filter(Number.isFinite) : undefined,
    }))
    .filter((evt: VCVLoopEvent) => evt.address && Number.isFinite(evt.t))
    .sort((a: VCVLoopEvent, b: VCVLoopEvent) => a.t - b.t);

  return {
    events,
    durationMs: Number(database.durationMs ?? database.metadata?.durationMs),
    playbackScaleRecommended: Number(database.playbackScaleRecommended),
  };
}

// Particle shader
const PARTICLE_VERTEX_SHADER = `
  attribute float size;
  attribute vec3 customColor;
  attribute float alpha;
  
  varying vec3 vColor;
  varying float vAlpha;
  
  void main() {
    vColor = customColor;
    vAlpha = alpha;
    
    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    gl_PointSize = size * (300.0 / -mvPosition.z);
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const PARTICLE_FRAGMENT_SHADER = `
  varying vec3 vColor;
  varying float vAlpha;
  
  void main() {
    vec2 center = gl_PointCoord - vec2(0.5);
    float dist = length(center);
    
    if (dist > 0.5) discard;
    
    float alpha = vAlpha * (1.0 - smoothstep(0.3, 0.5, dist));
    gl_FragColor = vec4(vColor, alpha);
  }
`;

interface HUDData {
  fps: number;
  frameTime: number;
  particleCount: number;
  behaviorMode: string;
  behaviorEnabled: boolean;
  paused: boolean;
  capturedCount: number;
  respawnEnabled: boolean;
  vcvMode: string;
  gravityStrength: number;
  timeScale: number;
  vcvLoopStatus: string;
  vcvLoopEventCount: number;
  vcvLoopDurationSec: number;
}

interface VCVLoopEvent {
  t: number;
  address: string;
  args: number[];
  ch?: number;
  value?: number;
  values?: number[];
}

export default function RealMathUniverse() {
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animationIdRef = useRef<number | null>(null);

  // State
  const [hudData, setHudData] = useState<HUDData>({
    fps: 60,
    frameTime: 16,
    particleCount: 0,
    behaviorMode: "none/manual",
    behaviorEnabled: true,
    paused: false,
    capturedCount: 0,
    respawnEnabled: true,
    vcvMode: "simulated",
    gravityStrength: 0.3,
    timeScale: DEFAULT_TIME_SCALE,
    vcvLoopStatus: "not loaded",
    vcvLoopEventCount: 0,
    vcvLoopDurationSec: 0,
  });
  const [hudVisible, setHudVisible] = useState(true);
  const [vcvPanelVisible, setVcvPanelVisible] = useState(false);
  const [captureMode, setCaptureMode] = useState(false);
  const lastTouchTapRef = useRef<{ time: number; x: number; y: number } | null>(
    null,
  );
  const [datasetStatus, setDatasetStatus] = useState<
    "synthetic" | "loaded" | "loading"
  >("synthetic");
  const [datasetRowCount, setDatasetRowCount] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const originalPositionsRef = useRef<THREE.Vector3[]>([]);

  // Simulation state refs
  const behaviorModeRef = useRef<BehaviorMode>(BehaviorMode.STABLE_ORBIT);
  const behaviorEnabledRef = useRef(true);
  const pausedRef = useRef(false);
  const respawnEnabledRef = useRef(true);
  const capturedCountRef = useRef(0);
  const gravityCenterRef = useRef(new THREE.Vector3(0, 0, 0));
  const gravityStrengthRef = useRef(0.3);
  const gravityStrengthTargetRef = useRef(0.3);
  const turbulenceRef = useRef(0.1);
  const timeScaleRef = useRef(getInitialTimeScale());
  const simulationClockRef = useRef(0);

  // Browser WebSocket bridge for VCV Rack UDP OSC
  const wsRef = useRef<WebSocket | null>(null);
  const [vcvConnected, setVcvConnected] = useState(false);
  const [vcvModeLabel, setVcvModeLabel] = useState("BRIDGE DISCONNECTED");
  const [vcvLastAddress, setVcvLastAddress] = useState("N/A");
  const [vcvLastValue, setVcvLastValue] = useState("N/A");
  const vcvLastMessageRef = useRef<number>(0);

  // Public website VCV database playback. A 30-second studio VCV bridge CSV recording can
  // be uploaded as public/data/rmu_vcv_capture_30s.csv and then stretched out at
  // an eye-friendly pace for website visitors who do not have local VCV Rack.
  const [vcvLoopStatus, setVcvLoopStatus] = useState<
    "not loaded" | "loading" | "loaded" | "missing" | "error"
  >("not loaded");
  const vcvLoopEventsRef = useRef<VCVLoopEvent[]>([]);
  const vcvLoopIndexRef = useRef(0);
  const vcvLoopDurationMsRef = useRef(0);
  const vcvLoopPlaybackStartRef = useRef<number | null>(null);
  const vcvLoopScaleRef = useRef(getInitialVCVLoopScale());
  const vcvLoopLastAppliedAddressRef = useRef("N/A");
  const vcvLoopLastAppliedValueRef = useRef("N/A");

  // Modulation state (VCV channels)
  const modulation = useRef({
    radialField: 0.3,
    orbitalField: 0.4,
    probabilityBankA: 0.5,
    shellField: 0.5,
    colorModeA: 0,
    colorModeB: 0,
    sceneIndex: 0,
    speedBankA: [] as number[],
    speedBankB: [] as number[],
    massBankA: [] as number[],
    massBankB: [] as number[],
    colorHSLBankA: [] as number[],
    colorHSLBankB: [] as number[],
    externalBehaviorAuthority: false,
    verticalField: 0.1,
    turbulenceField: 0.1,
    probability: 0.5,
  });

  const modulationTarget = useRef({
    radialField: 0.3,
    orbitalField: 0.4,
    probabilityBankA: 0.5,
    shellField: 0.5,
    colorModeA: 0,
    colorModeB: 0,
    sceneIndex: 0,
    speedBankA: [] as number[],
    speedBankB: [] as number[],
    massBankA: [] as number[],
    massBankB: [] as number[],
    colorHSLBankA: [] as number[],
    colorHSLBankB: [] as number[],
    externalBehaviorAuthority: false,
    verticalField: 0.1,
    turbulenceField: 0.1,
    probability: 0.5,
  });

  // Performance tracking
  const lastTimeRef = useRef(0);
  const frameCountRef = useRef(0);
  const fpsRef = useRef(60);
  const lastHudUpdateRef = useRef(0);

  // Initialize scene
  useEffect(() => {
    if (!containerRef.current) return;

    // Create renderer
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: "high-performance",
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x050510, 1);
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Create scene
    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0x050510, 0.0008);
    sceneRef.current = scene;

    // Create camera
    const camera = new THREE.PerspectiveCamera(
      60,
      window.innerWidth / window.innerHeight,
      0.1,
      10000,
    );
    camera.position.set(0, 100, 400);
    cameraRef.current = camera;

    // Create controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.minDistance = 10;
    controls.maxDistance = 2000;
    controlsRef.current = controls;

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.3);
    scene.add(ambientLight);

    const pointLight = new THREE.PointLight(0xffffff, 1.0, 1000);
    pointLight.position.set(0, 100, 100);
    scene.add(pointLight);

    // Create gravity well marker
    const wellGeometry = new THREE.SphereGeometry(5, 16, 16);
    const wellMaterial = new THREE.MeshBasicMaterial({
      color: 0xff0000,
      transparent: true,
      opacity: 0.5,
      wireframe: true,
    });
    const gravityWellMarker = new THREE.Mesh(wellGeometry, wellMaterial);
    gravityWellMarker.name = "gravityWell";
    scene.add(gravityWellMarker);

    // Create curvature grid
    const gridGroup = new THREE.Group();
    gridGroup.name = "curvatureGrid";
    for (let i = 1; i <= 10; i++) {
      const radius = i * 30;
      const geometry = new THREE.RingGeometry(radius - 0.5, radius + 0.5, 64);
      const material = new THREE.MeshBasicMaterial({
        color: 0x1a1a2e,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.3,
      });
      const ring = new THREE.Mesh(geometry, material);
      ring.rotation.x = -Math.PI / 2;
      gridGroup.add(ring);
    }
    scene.add(gridGroup);

    // Initialize particles
    initializeParticles(scene);

    // Website deployment mode: automatically load the public dataset when present.
    // Put the real file at public/data/merged_navdata.csv before building, or upload it to
    // /rmu-simulator/data/merged_navdata.csv after FileZilla deployment.
    const autoLoadDataset = () => {
      const params = new URLSearchParams(window.location.search);
      if (params.get("autoload") === "0") return;
      const datasetName = params.get("dataset") || "merged_navdata.csv";
      const datasetUrl = getRmuDataUrl(datasetName);
      setDatasetStatus("loading");
      fetch(datasetUrl, { cache: "force-cache" })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Dataset not found at ${datasetUrl}`);
          }
          return response.text();
        })
        .then((text) => {
          if (text.trim().length > 0) {
            console.log(`[RMU] Loaded website dataset: ${datasetUrl}`);
            loadCSVData(text);
          }
        })
        .catch((error) => {
          console.log("[RMU] Website dataset autoload skipped:", error.message);
          setDatasetStatus("synthetic");
        });
    };

    window.setTimeout(autoLoadDataset, 300);
    window.setTimeout(loadVCVCaptureLoop, 500);

    // Start animation
    lastTimeRef.current = performance.now();
    animate();

    // Handle resize
    const handleResize = () => {
      if (!cameraRef.current || !rendererRef.current) return;
      cameraRef.current.aspect = window.innerWidth / window.innerHeight;
      cameraRef.current.updateProjectionMatrix();
      rendererRef.current.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener("resize", handleResize);

    // Setup WebSocket bridge for VCV Rack UDP OSC.
    // Important: VCV sends UDP OSC to 127.0.0.1:9100. Browsers cannot listen to UDP.
    // Run `npm run bridge` to translate UDP OSC from port 9100 into WebSocket JSON on ws://127.0.0.1:8787/vcv.
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let manualClose = false;

    const connectVCV = () => {
      try {
        const ws = new WebSocket("ws://127.0.0.1:8787/vcv");
        wsRef.current = ws;
        setVcvModeLabel("CONNECTING TO BRIDGE");

        ws.onopen = () => {
          console.log(
            "[RMU] VCV OSC WebSocket bridge connected at ws://127.0.0.1:8787/vcv",
          );
          setVcvConnected(true);
          setVcvModeLabel("BRIDGE ONLINE / WAITING OSC");
        };

        ws.onmessage = (event) => {
          vcvLastMessageRef.current = Date.now();
          try {
            const data = JSON.parse(event.data);

            if (data.type === "bridge_status") {
              setVcvConnected(Boolean(data.bridgeOnline));
              if (data.vcvLive) {
                setVcvModeLabel("VCV LIVE");
                if (typeof data.lastOscAddress === "string")
                  setVcvLastAddress(data.lastOscAddress);
                if (data.lastOscValue !== undefined)
                  setVcvLastValue(String(data.lastOscValue));
              } else if (data.udpInputReady) {
                setVcvModeLabel("BRIDGE ONLINE / WAITING OSC");
              } else {
                setVcvModeLabel("BRIDGE ONLINE / UDP NOT READY");
              }
              return;
            }

            // Bridge OSC-style messages: { type: "osc", address: "/ch/1", args: [0.5] }
            if (data.address && Array.isArray(data.args)) {
              setVcvLastAddress(data.address);
              setVcvLastValue(
                data.args
                  .map((v: unknown) =>
                    typeof v === "number" ? v.toFixed(3) : String(v),
                  )
                  .join(", "),
              );
              setVcvModeLabel("VCV LIVE");
              setVcvConnected(true);
              handleOSCMessage(data.address, data.args);
            }
            // Direct channel updates are also supported: { ch: 1, value: 0.5 } or { ch: 1, values: [...] }
            else if (data.ch !== undefined) {
              const channelLabel = `/ch/${data.ch}`;
              setVcvLastAddress(channelLabel);
              if (Array.isArray(data.values)) {
                setVcvLastValue(
                  data.values
                    .map((v: unknown) =>
                      typeof v === "number" ? v.toFixed(3) : String(v),
                    )
                    .join(", "),
                );
                setVcvModeLabel("VCV LIVE");
                setVcvConnected(true);
                handleChannelPoly(data.ch, data.values);
              } else if (data.value !== undefined) {
                setVcvLastValue(
                  typeof data.value === "number"
                    ? data.value.toFixed(3)
                    : String(data.value),
                );
                setVcvModeLabel("VCV LIVE");
                setVcvConnected(true);
                handleChannelMono(data.ch, data.value);
              }
            }
          } catch (e) {
            console.log("[RMU] VCV bridge message parse error:", e, event.data);
          }
        };

        ws.onclose = () => {
          setVcvConnected(false);
          setVcvModeLabel("BRIDGE DISCONNECTED");
          if (!manualClose) {
            console.log("[RMU] VCV bridge disconnected, retrying in 3s...");
            reconnectTimer = setTimeout(connectVCV, 3000);
          }
        };

        ws.onerror = (err) => {
          console.log(
            "[RMU] VCV bridge WebSocket error. Is `npm run bridge` running?",
            err,
          );
          ws.close();
        };
      } catch (e) {
        console.log("[RMU] VCV bridge connection failed, retrying in 3s...", e);
        reconnectTimer = setTimeout(connectVCV, 3000);
      }
    };

    connectVCV();

    // Cleanup
    return () => {
      window.removeEventListener("resize", handleResize);
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
      manualClose = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
      renderer.dispose();
      if (containerRef.current && renderer.domElement) {
        containerRef.current.removeChild(renderer.domElement);
      }
    };
  }, []);

  // Handle OSC messages from VCV
  const handleOSCMessage = (address: string, args: number[]) => {
    // Parse channel from address like "/ch/1"
    const match = address.match(/\/ch\/(\d+)/);
    if (match) {
      const ch = parseInt(match[1]);
      if (args.length > 1) {
        handleChannelPoly(ch, args);
      } else {
        handleChannelMono(ch, args[0] || 0);
      }
    }
  };

  const handleChannelMono = (ch: number, value: number) => {
    // Normalize value (VCV uses -5 to 5 or 0 to 10)
    const normalized = Math.max(0, Math.min(1, (value + 5) / 10));

    switch (ch) {
      case 1:
        modulationTarget.current.probabilityBankA = normalized;
        break;
      case 2:
        modulationTarget.current.radialField = normalized;
        break;
      case 3:
        modulationTarget.current.orbitalField = normalized;
        break;
      case 4:
        modulationTarget.current.verticalField = normalized;
        break;
      case 5:
        modulationTarget.current.turbulenceField = normalized;
        break;
      case 6:
        modulationTarget.current.shellField = normalized;
        break;
      case 7:
        modulationTarget.current.colorModeA = Math.floor(normalized * 8);
        break;
      case 8:
        modulationTarget.current.sceneIndex = Math.floor(normalized * 8);
        break;
      case 14:
        gravityCenterRef.current.x = (normalized - 0.5) * 200;
        break;
      case 15:
        gravityStrengthTargetRef.current = normalized;
        break;
      case 18: // Behavior code
        const newMode = Math.floor(normalized * 8);
        if (newMode >= 0 && newMode <= 7) {
          behaviorModeRef.current = newMode as BehaviorMode;
        }
        break;
      case 19: // Behavior authority gate - if high, VCV controls behavior
        modulationTarget.current.externalBehaviorAuthority = normalized >= 0.5;
        break;
    }
  };

  const handleChannelPoly = (ch: number, values: number[]) => {
    // Poly channels for species banks (16 voices per bank)
    const normalized = values.map((v) =>
      Math.max(0, Math.min(1, (v + 5) / 10)),
    );

    switch (ch) {
      case 9: // Speed bank A (species 1-16)
        modulationTarget.current.speedBankA = normalized;
        break;
      case 10: // Mass bank A (species 1-16)
        modulationTarget.current.massBankA = normalized;
        break;
      case 11: // Mass bank B (species 17-22)
        modulationTarget.current.massBankB = normalized;
        break;
      case 16: // Color HSL bank A
        modulationTarget.current.colorHSLBankA = normalized;
        break;
      case 17: // Color HSL bank B
        modulationTarget.current.colorHSLBankB = normalized;
        break;
    }
  };

  // Initialize particles
  const initializeParticles = (scene: THREE.Scene) => {
    const particleCount = getInitialParticleCount();
    const particles: Particle[] = [];

    // Create geometry and attributes
    const positions = new Float32Array(particleCount * 3);
    const colors = new Float32Array(particleCount * 3);
    const sizes = new Float32Array(particleCount);
    const alphas = new Float32Array(particleCount);

    for (let i = 0; i < particleCount; i++) {
      // Assign species deterministically
      const speciesId = (i % 22) + 1;
      const species = SPECIES_REGISTRY[speciesId - 1];

      // Generate position in sphere
      const radius = 50 + Math.random() * 200;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);

      const x = radius * Math.sin(phi) * Math.cos(theta);
      const y = radius * Math.sin(phi) * Math.sin(theta);
      const z = radius * Math.cos(phi);

      const particle: Particle = {
        position: new THREE.Vector3(x, y, z),
        velocity: new THREE.Vector3(
          (Math.random() - 0.5) * 2,
          (Math.random() - 0.5) * 2,
          (Math.random() - 0.5) * 2,
        ),
        acceleration: new THREE.Vector3(0, 0, 0),
        speciesId,
        familyId: Math.floor(speciesId / 5),
        baseMass: species.baseMass,
        effectiveMass: species.baseMass,
        charge: species.charge,
        color: species.color.clone(),
        lifetime: 1000 + Math.random() * 500,
        probabilityWeight: 0.5 + Math.random() * 0.5,
        speedMultiplier: species.speedMultiplier,
        turbulenceResponse: species.turbulenceResponse,
        cohesionResponse: species.cohesionResponse,
        fieldResponse: { ...species.fieldCouplings },
      };

      particles.push(particle);

      // Set buffer data
      const i3 = i * 3;
      positions[i3] = x;
      positions[i3 + 1] = y;
      positions[i3 + 2] = z;
      colors[i3] = species.color.r;
      colors[i3 + 1] = species.color.g;
      colors[i3 + 2] = species.color.b;
      sizes[i] = 3 * (0.5 + species.baseMass * 0.1);
      alphas[i] = 0.7 + Math.random() * 0.3;
    }

    particlesRef.current = particles;

    // Create particle geometry
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute("customColor", new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute("size", new THREE.BufferAttribute(sizes, 1));
    geometry.setAttribute("alpha", new THREE.BufferAttribute(alphas, 1));

    // Create material
    const material = new THREE.ShaderMaterial({
      uniforms: {},
      vertexShader: PARTICLE_VERTEX_SHADER,
      fragmentShader: PARTICLE_FRAGMENT_SHADER,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    // Create points
    const points = new THREE.Points(geometry, material);
    points.name = "particles";
    scene.add(points);

    setHudData((prev) => ({ ...prev, particleCount }));
  };

  // Load a captured VCV database for public website playback. This lets the
  // deployed site behave like VCV is driving it even though a public browser
  // cannot receive local UDP OSC from VCV Rack.
  const loadVCVCaptureLoop = () => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("vcvloop") === "0") {
      setVcvLoopStatus("missing");
      return;
    }

    // Default public database is the fast frame JSON generated from ~/Documents/rmu_vcv_capture_30s.csv.
    // Full local source path on Joe's Mac:
    //   /Users/Joe/Documents/rmu_vcv_capture_30s.csv
    // Website deployment path after FileZilla upload:
    //   /rmu-simulator/data/vcv_capture_loop_fast.json
    // Optional full CSV fallback:
    //   /rmu-simulator/data/rmu_vcv_capture_30s.csv
    const loopName = params.get("vcvdb") || "vcv_capture_loop_fast.json";
    const loopUrlCandidates = getRmuDataUrlCandidates(loopName);
    setVcvLoopStatus("loading");
    console.log("[RMU] Attempting VCV capture loop paths:", loopUrlCandidates);

    fetchFirstAvailableText(loopUrlCandidates)
      .then(({ text: databaseText, url: loadedUrl }) => {
        const trimmed = databaseText.trim();
        const parsed = loopName.toLowerCase().endsWith(".json") || trimmed.startsWith("{")
          ? parseVCVCaptureJSON(trimmed)
          : { events: parseVCVCaptureCSV(trimmed) };

        const events = parsed.events;
        if (events.length === 0)
          throw new Error("VCV loop database contains no usable events");

        const computedDuration = events[events.length - 1].t - events[0].t;
        const durationMs = Number.isFinite(parsed.durationMs as number)
          ? Number(parsed.durationMs)
          : Math.max(1000, computedDuration || events[events.length - 1].t || 30000);

        vcvLoopEventsRef.current = events;
        vcvLoopIndexRef.current = 0;
        vcvLoopDurationMsRef.current = Math.max(1000, durationMs);
        vcvLoopPlaybackStartRef.current = performance.now();

        if (Number.isFinite(Number(parsed.playbackScaleRecommended))) {
          vcvLoopScaleRef.current = clampNumber(
            Number(parsed.playbackScaleRecommended),
            1.0,
            30.0,
          );
        }

        setVcvLoopStatus("loaded");
        console.log(
          `[RMU] Loaded VCV capture loop: ${loadedUrl} (${events.length} events, ${(durationMs / 1000).toFixed(1)}s captured, ${vcvLoopScaleRef.current.toFixed(1)}x playback stretch)`,
        );
      })
      .catch((error) => {
        console.log("[RMU] VCV capture loop autoload skipped:", error.message);
        setVcvLoopStatus("missing");
      });
  };

  const applyVCVLoopEvent = (evt: VCVLoopEvent) => {
    const args =
      evt.args.length > 0
        ? evt.args
        : Array.isArray(evt.values)
          ? evt.values
          : evt.value !== undefined
            ? [evt.value]
            : [];

    if (args.length === 0) return;

    if (evt.ch !== undefined && Number.isFinite(evt.ch)) {
      if (args.length > 1) handleChannelPoly(evt.ch, args);
      else handleChannelMono(evt.ch, args[0]);
    } else {
      handleOSCMessage(evt.address, args);
    }

    vcvLoopLastAppliedAddressRef.current = evt.address;
    vcvLoopLastAppliedValueRef.current = args
      .map((v) => (Number.isFinite(v) ? v.toFixed(3) : String(v)))
      .join(", ");
  };

  const updateVCVLoopPlayback = () => {
    const events = vcvLoopEventsRef.current;
    const durationMs = vcvLoopDurationMsRef.current;
    const start = vcvLoopPlaybackStartRef.current;

    // Do not let the public VCV database fight live studio VCV. Live OSC always wins.
    if (
      vcvModeLabel === "VCV LIVE" &&
      Date.now() - vcvLastMessageRef.current < 3000
    )
      return;
    if (
      events.length === 0 ||
      durationMs <= 0 ||
      start === null ||
      pausedRef.current
    )
      return;

    const stretchedDuration = durationMs * vcvLoopScaleRef.current;
    const elapsed = (performance.now() - start) % stretchedDuration;
    const capturePosition = (elapsed / stretchedDuration) * durationMs;

    if (
      vcvLoopIndexRef.current >= events.length ||
      events[vcvLoopIndexRef.current].t > capturePosition
    ) {
      vcvLoopIndexRef.current = 0;
    }

    let applied = 0;
    while (
      vcvLoopIndexRef.current < events.length &&
      events[vcvLoopIndexRef.current].t <= capturePosition &&
      applied < 48
    ) {
      applyVCVLoopEvent(events[vcvLoopIndexRef.current]);
      vcvLoopIndexRef.current += 1;
      applied += 1;
    }
  };

  // Animation loop
  const animate = () => {
    animationIdRef.current = requestAnimationFrame(animate);

    const now = performance.now();
    const deltaTime = now - lastTimeRef.current;
    lastTimeRef.current = now;

    // Calculate FPS
    frameCountRef.current++;
    if (frameCountRef.current % 10 === 0) {
      fpsRef.current = 1000 / deltaTime;
    }

    // Update controls
    controlsRef.current?.update();

    // Update simulation if not paused.  The public website version uses a
    // cinematic time scale so the field breathes like an instrument panel
    // instead of violently changing every frame.
    if (!pausedRef.current && behaviorEnabledRef.current) {
      const rawDt = Math.min(deltaTime / 1000, 0.05);
      const dt = rawDt * timeScaleRef.current;
      simulationClockRef.current += dt;
      updateVCVLoopPlayback();
      smoothRuntimeControls(dt);
      updateSimulation(dt);
    }

    // Render
    if (rendererRef.current && sceneRef.current && cameraRef.current) {
      rendererRef.current.render(sceneRef.current, cameraRef.current);
    }

    // Update HUD at a controlled rate. Updating React state every animation
    // frame can cause browser jank, duplicate WebSocket churn in dev, and the
    // appearance of a frozen simulation even while requestAnimationFrame is alive.
    if (now - lastHudUpdateRef.current >= HUD_UPDATE_INTERVAL_MS) {
      lastHudUpdateRef.current = now;
      setHudData((prev) => ({
        ...prev,
        fps: fpsRef.current,
        frameTime: deltaTime,
        behaviorMode: BEHAVIOR_NAMES[behaviorModeRef.current],
        behaviorEnabled: behaviorEnabledRef.current,
        paused: pausedRef.current,
        capturedCount: capturedCountRef.current,
        respawnEnabled: respawnEnabledRef.current,
        gravityStrength: gravityStrengthRef.current,
        timeScale: timeScaleRef.current,
        vcvLoopStatus,
        vcvLoopEventCount: vcvLoopEventsRef.current.length,
        vcvLoopDurationSec: vcvLoopDurationMsRef.current / 1000,
      }));
    }
  };

  // Smooth live control changes so VCV, sliders, and website defaults move like
  // damped marine instrumentation rather than snapping on every incoming packet.
  const smoothRuntimeControls = (dt: number) => {
    const modFactor = 1 - Math.exp(-MODULATION_SMOOTHING_RATE * dt);
    const gravFactor = 1 - Math.exp(-GRAVITY_SMOOTHING_RATE * dt);
    const current = modulation.current;
    const target = modulationTarget.current;

    current.radialField +=
      (target.radialField - current.radialField) * modFactor;
    current.orbitalField +=
      (target.orbitalField - current.orbitalField) * modFactor;
    current.verticalField +=
      (target.verticalField - current.verticalField) * modFactor;
    current.turbulenceField +=
      (target.turbulenceField - current.turbulenceField) * modFactor;
    current.probability +=
      (target.probability - current.probability) * modFactor;
    current.probabilityBankA +=
      (target.probabilityBankA - current.probabilityBankA) * modFactor;
    current.shellField += (target.shellField - current.shellField) * modFactor;

    const smoothBank = (dst: number[], src: number[]) => {
      if (dst.length !== src.length) {
        dst.length = src.length;
        for (let i = 0; i < src.length; i++) dst[i] = src[i];
        return;
      }
      for (let i = 0; i < src.length; i++) {
        dst[i] += (src[i] - dst[i]) * modFactor;
      }
    };

    smoothBank(current.speedBankA, target.speedBankA);
    smoothBank(current.speedBankB, target.speedBankB);
    smoothBank(current.massBankA, target.massBankA);
    smoothBank(current.massBankB, target.massBankB);
    smoothBank(current.colorHSLBankA, target.colorHSLBankA);
    smoothBank(current.colorHSLBankB, target.colorHSLBankB);

    gravityStrengthRef.current +=
      (gravityStrengthTargetRef.current - gravityStrengthRef.current) *
      gravFactor;
  };

  // Update simulation
  const updateSimulation = (dt: number) => {
    const particles = particlesRef.current;
    const scene = sceneRef.current;
    if (!particles.length || !scene) return;

    const pointsObj = scene.getObjectByName("particles") as THREE.Points;
    if (!pointsObj) return;

    const positions = pointsObj.geometry.getAttribute(
      "position",
    ) as THREE.BufferAttribute;
    const tempVec = new THREE.Vector3();
    const tempVec2 = new THREE.Vector3();

    for (let i = 0; i < particles.length; i++) {
      const p = particles[i];

      // Apply behavior based on mode
      switch (behaviorModeRef.current) {
        case BehaviorMode.STABLE_ORBIT:
          applyOrbitBehavior(p, dt, tempVec, tempVec2);
          break;
        case BehaviorMode.BLACK_HOLE:
          applyBlackHoleBehavior(p, dt, tempVec);
          break;
        case BehaviorMode.EXPANSION:
          applyExpansionBehavior(p, dt, tempVec);
          break;
        case BehaviorMode.TURBULENCE_BURST:
          applyTurbulenceBehavior(p, dt);
          break;
        case BehaviorMode.COLLAPSE:
          applyCollapseBehavior(p, dt, tempVec, tempVec2);
          break;
        case BehaviorMode.GEOSPATIAL_STATIC:
          p.velocity.set(0, 0, 0);
          break;
        case BehaviorMode.GEOSPATIAL_LIVE:
          applyGeospatialBehavior(p, dt);
          break;
        default:
          applyManualBehavior(p, dt, tempVec, tempVec2);
      }

      // Apply damping (reduced to prevent stopping)
      p.velocity.multiplyScalar(0.995);

      // Speed limits
      const speed = p.velocity.length();
      if (speed > 50) {
        p.velocity.multiplyScalar(50 / speed);
      }

      // Add minimum movement to prevent complete stop
      if (
        speed < 0.1 &&
        behaviorModeRef.current !== BehaviorMode.GEOSPATIAL_STATIC
      ) {
        p.velocity.x += (Math.random() - 0.5) * 0.2;
        p.velocity.y += (Math.random() - 0.5) * 0.2;
        p.velocity.z += (Math.random() - 0.5) * 0.2;
      }

      // Update position
      p.position.addScaledVector(p.velocity, dt * 60);

      // Update buffer
      const i3 = i * 3;
      positions.array[i3] = p.position.x;
      positions.array[i3 + 1] = p.position.y;
      positions.array[i3 + 2] = p.position.z;
    }

    positions.needsUpdate = true;

    // Update gravity well marker
    const wellMarker = scene.getObjectByName("gravityWell") as THREE.Mesh;
    if (wellMarker) {
      wellMarker.position.copy(gravityCenterRef.current);
      wellMarker.scale.setScalar(1 + gravityStrengthRef.current * 2);
    }
  };

  // Behavior implementations
  const applyOrbitBehavior = (
    p: Particle,
    dt: number,
    tempVec: THREE.Vector3,
    tempVec2: THREE.Vector3,
  ) => {
    tempVec.copy(p.position).sub(gravityCenterRef.current);
    const dist = tempVec.length();

    if (dist < 1) return;

    const shellIndex = p.speciesId % 8;
    const targetRadius = 30 + shellIndex * 25 + (p.baseMass % 10);

    const radialError = dist - targetRadius;
    tempVec.normalize();
    // Stronger radial correction
    p.velocity.addScaledVector(tempVec, -radialError * 0.05 * dt * 60);

    // Create orbital velocity perpendicular to radial direction
    tempVec2.set(-tempVec.z, tempVec.y * 0.1, tempVec.x).normalize();
    const orbitalSpeed = (2.0 / Math.sqrt(dist / 50)) * p.speedMultiplier;
    p.velocity.addScaledVector(tempVec2, orbitalSpeed * dt * 60);
  };

  const applyBlackHoleBehavior = (
    p: Particle,
    dt: number,
    tempVec: THREE.Vector3,
  ) => {
    tempVec.copy(gravityCenterRef.current).sub(p.position);
    const dist = tempVec.length();

    if (dist < 5) {
      capturedCountRef.current++;
      if (respawnEnabledRef.current) {
        respawnParticle(p);
      } else {
        p.position.copy(gravityCenterRef.current);
        p.velocity.set(0, 0, 0);
      }
      return;
    }

    const softDist = Math.max(dist, 5);
    const force = (5000 * gravityStrengthRef.current) / (softDist * softDist);

    tempVec.normalize();
    p.velocity.addScaledVector(tempVec, force * dt * 60);
  };

  const applyExpansionBehavior = (
    p: Particle,
    dt: number,
    tempVec: THREE.Vector3,
  ) => {
    tempVec.copy(p.position).sub(gravityCenterRef.current);
    const dist = tempVec.length();

    if (dist < 0.001) {
      tempVec
        .set(Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5)
        .normalize();
    } else {
      tempVec.normalize();
    }

    const expansionForce = 1.5 * (1 - dist / 500);
    if (expansionForce > 0) {
      p.velocity.addScaledVector(
        tempVec,
        expansionForce * p.speedMultiplier * dt * 60,
      );
    }
  };

  const applyTurbulenceBehavior = (p: Particle, dt: number) => {
    const time = simulationClockRef.current;
    const intensity = 8.0;
    const freq = 0.02;

    const px = p.position.x * freq + time * 2;
    const py = p.position.y * freq + time * 2.2;
    const pz = p.position.z * freq + time * 1.8;

    p.velocity.x += Math.sin(px) * intensity * p.turbulenceResponse * dt * 60;
    p.velocity.y += Math.sin(py) * intensity * p.turbulenceResponse * dt * 60;
    p.velocity.z += Math.sin(pz) * intensity * p.turbulenceResponse * dt * 60;
  };

  const applyCollapseBehavior = (
    p: Particle,
    dt: number,
    tempVec: THREE.Vector3,
    tempVec2: THREE.Vector3,
  ) => {
    tempVec.copy(gravityCenterRef.current).sub(p.position);
    const dist = tempVec.length();

    if (dist < 10) {
      p.velocity.multiplyScalar(0.95);
      return;
    }

    tempVec.normalize();
    p.velocity.addScaledVector(tempVec, 1.5 * dt * 60);

    tempVec2.set(-tempVec.z, 0, tempVec.x);
    p.velocity.addScaledVector(tempVec2, 0.5 * dt * 60);
  };

  const applyGeospatialBehavior = (p: Particle, dt: number) => {
    const localTurb = 0.1 + turbulenceRef.current * p.turbulenceResponse;
    p.velocity.x += (Math.random() - 0.5) * localTurb * dt;
    p.velocity.y += (Math.random() - 0.5) * localTurb * 0.5 * dt;
    p.velocity.z += (Math.random() - 0.5) * localTurb * dt;
  };

  const applyManualBehavior = (
    p: Particle,
    dt: number,
    tempVec: THREE.Vector3,
    tempVec2: THREE.Vector3,
  ) => {
    tempVec.copy(p.position).sub(gravityCenterRef.current);
    const dist = tempVec.length();

    if (dist > 0.001) {
      tempVec.normalize();
      const radialForce =
        (modulation.current.radialField - 0.5) * 2 * p.fieldResponse.curvature;
      p.velocity.addScaledVector(tempVec, radialForce * dt);

      tempVec2.set(-tempVec.z, 0, tempVec.x);
      const orbitalForce =
        (modulation.current.orbitalField - 0.5) * 2 * p.speedMultiplier;
      p.velocity.addScaledVector(tempVec2, orbitalForce * dt);
    }

    const verticalForce = (modulation.current.verticalField - 0.5) * 2;
    p.velocity.y += verticalForce * dt;

    if (modulation.current.turbulenceField > 0.01) {
      p.velocity.x +=
        (Math.random() - 0.5) *
        modulation.current.turbulenceField *
        p.turbulenceResponse *
        dt;
      p.velocity.y +=
        (Math.random() - 0.5) *
        modulation.current.turbulenceField *
        p.turbulenceResponse *
        dt;
      p.velocity.z +=
        (Math.random() - 0.5) *
        modulation.current.turbulenceField *
        p.turbulenceResponse *
        dt;
    }
  };

  const respawnParticle = (p: Particle) => {
    const radius = 200 + Math.random() * 100;
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1);

    p.position.set(
      radius * Math.sin(phi) * Math.cos(theta),
      radius * Math.sin(phi) * Math.sin(theta),
      radius * Math.cos(phi),
    );

    p.velocity.copy(p.position).normalize().multiplyScalar(-0.5);
  };

  // Keyboard handlers
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement) return;

      if (e.shiftKey) {
        switch (e.code) {
          case "KeyE":
            e.preventDefault();
            behaviorEnabledRef.current = !behaviorEnabledRef.current;
            break;
          case "KeyR":
            e.preventDefault();
            resetParticles();
            break;
        }
        return;
      }

      switch (e.code) {
        case "Space":
          e.preventDefault();
          pausedRef.current = !pausedRef.current;
          if (behaviorModeRef.current === BehaviorMode.GEOSPATIAL_STATIC) {
            behaviorModeRef.current = BehaviorMode.GEOSPATIAL_LIVE;
          } else if (behaviorModeRef.current === BehaviorMode.GEOSPATIAL_LIVE) {
            behaviorModeRef.current = BehaviorMode.GEOSPATIAL_STATIC;
          }
          break;
        case "Digit0":
        case "Numpad0":
          behaviorModeRef.current = BehaviorMode.NONE;
          break;
        case "Digit1":
        case "Numpad1":
          behaviorModeRef.current = BehaviorMode.STABLE_ORBIT;
          break;
        case "Digit2":
        case "Numpad2":
          behaviorModeRef.current = BehaviorMode.BLACK_HOLE;
          break;
        case "Digit3":
        case "Numpad3":
          behaviorModeRef.current = BehaviorMode.GEOSPATIAL_STATIC;
          break;
        case "Digit4":
        case "Numpad4":
          behaviorModeRef.current = BehaviorMode.GEOSPATIAL_LIVE;
          break;
        case "Digit5":
        case "Numpad5":
          behaviorModeRef.current = BehaviorMode.EXPANSION;
          break;
        case "Digit6":
        case "Numpad6":
          behaviorModeRef.current = BehaviorMode.TURBULENCE_BURST;
          break;
        case "Digit7":
        case "Numpad7":
          behaviorModeRef.current = BehaviorMode.COLLAPSE;
          break;
        case "KeyR":
          respawnEnabledRef.current = !respawnEnabledRef.current;
          break;
        case "KeyH":
          setHudVisible((v) => !v);
          break;
        case "KeyV":
          setVcvPanelVisible((v) => !v);
          break;
        case "KeyC":
          setCaptureMode((v) => {
            const newVal = !v;
            if (newVal) {
              setHudVisible(false);
              setVcvPanelVisible(false);
            }
            return newVal;
          });
          break;
        case "BracketLeft":
          timeScaleRef.current = clampNumber(
            timeScaleRef.current * 0.8,
            MIN_TIME_SCALE,
            MAX_TIME_SCALE,
          );
          break;
        case "BracketRight":
          timeScaleRef.current = clampNumber(
            timeScaleRef.current * 1.25,
            MIN_TIME_SCALE,
            MAX_TIME_SCALE,
          );
          break;
        case "KeyT":
          timeScaleRef.current = DEFAULT_TIME_SCALE;
          break;
        case "Escape":
          cameraRef.current?.position.set(0, 100, 400);
          controlsRef.current?.reset();
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const resetParticles = () => {
    const particles = particlesRef.current;
    const originals = originalPositionsRef.current;

    for (let i = 0; i < particles.length; i++) {
      if (originals.length > 0 && i < originals.length) {
        // Reset to original loaded positions
        particles[i].position.copy(originals[i]);
      } else {
        const radius = 50 + Math.random() * 200;
        const theta = Math.random() * Math.PI * 2;
        const phi = Math.acos(2 * Math.random() - 1);
        particles[i].position.set(
          radius * Math.sin(phi) * Math.cos(theta),
          radius * Math.sin(phi) * Math.sin(theta),
          radius * Math.cos(phi),
        );
      }
      particles[i].velocity.set(
        (Math.random() - 0.5) * 2,
        (Math.random() - 0.5) * 2,
        (Math.random() - 0.5) * 2,
      );
    }
    capturedCountRef.current = 0;
  };

  // Load CSV data and map to particles
  const loadCSVData = (csvText: string) => {
    setDatasetStatus("loading");

    const lines = csvText.trim().split("\n");
    const headers = lines[0]
      .split(",")
      .map((h) => h.trim().toLowerCase().replace(/['"]/g, ""));

    console.log("[v0] CSV Headers found:", headers);

    // Find column indices - more flexible matching
    const lonIdx = headers.findIndex(
      (h) =>
        h.includes("longitude") ||
        h.includes("long") ||
        h === "lon" ||
        h === "lng" ||
        h === "x",
    );
    const latIdx = headers.findIndex(
      (h) => h.includes("latitude") || h === "lat" || h === "y",
    );
    const elevIdx = headers.findIndex(
      (h) =>
        h.includes("elevation") ||
        h.includes("depth") ||
        h.includes("elev") ||
        h.includes("altitude") ||
        h.includes("alt") ||
        h === "z",
    );
    const densityIdx = headers.findIndex(
      (h) =>
        h.includes("density") ||
        h.includes("track") ||
        h.includes("count") ||
        h.includes("weight"),
    );

    console.log(
      "[v0] Column indices - lon:",
      lonIdx,
      "lat:",
      latIdx,
      "elev:",
      elevIdx,
      "density:",
      densityIdx,
    );

    // If we can't find lat/lon, try to use the first 2-3 numeric columns
    let useRawColumns = false;
    let col1 = lonIdx,
      col2 = latIdx,
      col3 = elevIdx;

    if (lonIdx === -1 || latIdx === -1) {
      console.log("[v0] Falling back to first numeric columns as x, y, z");
      useRawColumns = true;
      col1 = 0;
      col2 = 1;
      col3 = headers.length > 2 ? 2 : -1;
    }

    const dataRows = lines.slice(1).filter((line) => line.trim());
    const particles = particlesRef.current;
    const scene = sceneRef.current;

    if (!scene) return;

    // First pass: determine data ranges
    let minX = Infinity,
      maxX = -Infinity;
    let minY = Infinity,
      maxY = -Infinity;
    let minZ = Infinity,
      maxZ = -Infinity;

    const parsedData: { x: number; y: number; z: number; density: number }[] =
      [];

    for (const row of dataRows) {
      const cols = row.split(",").map((c) => c.trim().replace(/['"]/g, ""));

      const v1 = parseFloat(cols[col1]) || 0;
      const v2 = parseFloat(cols[col2]) || 0;
      const v3 = col3 !== -1 ? parseFloat(cols[col3]) || 0 : 0;
      const density = densityIdx !== -1 ? parseFloat(cols[densityIdx]) || 1 : 1;

      if (isNaN(v1) || isNaN(v2)) continue;

      parsedData.push({ x: v1, y: v3, z: v2, density });

      minX = Math.min(minX, v1);
      maxX = Math.max(maxX, v1);
      minY = Math.min(minY, v3);
      maxY = Math.max(maxY, v3);
      minZ = Math.min(minZ, v2);
      maxZ = Math.max(maxZ, v2);
    }

    console.log(
      "[v0] Data ranges - X:",
      minX,
      "to",
      maxX,
      "Y:",
      minY,
      "to",
      maxY,
      "Z:",
      minZ,
      "to",
      maxZ,
    );

    // Calibration mappings with auto-detected ranges.
    const mapValue = (
      val: number,
      inMin: number,
      inMax: number,
      outMin: number,
      outMax: number,
    ) => {
      if (inMax === inMin) return (outMin + outMax) / 2;
      const t = (val - inMin) / (inMax - inMin);
      return outMin + t * (outMax - outMin);
    };

    // v14 geographic calibration: if the CSV has real latitude/longitude columns,
    // preserve the actual relative geographic aspect ratio instead of stretching
    // longitude and latitude independently. Longitude is corrected by cos(latitude)
    // using a simple equirectangular projection, which is appropriate for the
    // Florida/Gulf regional field and keeps the track field geographically honest.
    const usingLatLon = !useRawColumns && lonIdx >= 0 && latIdx >= 0;
    const centerLon = (minX + maxX) * 0.5;
    const centerLat = (minZ + maxZ) * 0.5;
    const metersPerDegLat = 111_320;
    const metersPerDegLon = 111_320 * Math.cos((centerLat * Math.PI) / 180);
    const projected = parsedData.map((d) => ({
      px: usingLatLon ? (d.x - centerLon) * metersPerDegLon : d.x,
      pz: usingLatLon ? (d.z - centerLat) * metersPerDegLat : d.z,
      y: d.y,
      density: d.density,
    }));
    const maxAbsProjected = Math.max(
      1,
      ...projected.map((d) => Math.max(Math.abs(d.px), Math.abs(d.pz))),
    );
    const geoScale = 310 / maxAbsProjected;

    const newPositions: THREE.Vector3[] = [];
    const rowCount = Math.min(parsedData.length, particles.length);

    for (let i = 0; i < rowCount; i++) {
      const d = parsedData[i];
      const p = projected[i];

      // Map to 3D space. Lat/lon uses a single uniform scale so geography remains
      // correctly proportioned; non-geographic fallback keeps the previous auto-fit.
      const x = usingLatLon ? p.px * geoScale : mapValue(d.x, minX, maxX, -300, 300);
      const y = mapValue(d.y, minY, maxY, -150, 150);
      const z = usingLatLon ? p.pz * geoScale : mapValue(d.z, minZ, maxZ, -200, 200);

      particles[i].position.set(x, y, z);
      particles[i].velocity.set(
        (Math.random() - 0.5) * 0.5,
        (Math.random() - 0.5) * 0.5,
        (Math.random() - 0.5) * 0.5,
      );
      particles[i].probabilityWeight = Math.min(1, d.density / 100);

      newPositions.push(new THREE.Vector3(x, y, z));
    }

    // Fill remaining particles with random positions based on data bounds
    for (let i = rowCount; i < particles.length; i++) {
      const x = (Math.random() - 0.5) * 600;
      const y = (Math.random() - 0.5) * 300;
      const z = (Math.random() - 0.5) * 400;
      particles[i].position.set(x, y, z);
      particles[i].velocity.set(0, 0, 0);
      newPositions.push(new THREE.Vector3(x, y, z));
    }

    originalPositionsRef.current = newPositions;

    // Update geometry
    const pointsObj = scene.getObjectByName("particles") as THREE.Points;
    if (pointsObj) {
      const positions = pointsObj.geometry.getAttribute(
        "position",
      ) as THREE.BufferAttribute;
      for (let i = 0; i < particles.length; i++) {
        const i3 = i * 3;
        positions.array[i3] = particles[i].position.x;
        positions.array[i3 + 1] = particles[i].position.y;
        positions.array[i3 + 2] = particles[i].position.z;
      }
      positions.needsUpdate = true;
    }

    setDatasetRowCount(rowCount);
    setDatasetStatus("loaded");

    // Switch to geospatial mode
    behaviorModeRef.current = BehaviorMode.GEOSPATIAL_STATIC;
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      if (text) {
        loadCSVData(text);
      }
    };
    reader.readAsText(file);
  };

  const handleRootTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    if (captureMode) return;
    if (event.changedTouches.length !== 1) return;

    const target = event.target as HTMLElement | null;
    if (
      target?.closest(
        "button, input, select, textarea, a, [data-rmu-ignore-doubletap='true']",
      )
    ) {
      return;
    }

    const touch = event.changedTouches[0];
    const now = Date.now();
    const previous = lastTouchTapRef.current;

    if (previous) {
      const dt = now - previous.time;
      const dx = touch.clientX - previous.x;
      const dy = touch.clientY - previous.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (dt > 40 && dt < 360 && distance < 44) {
        event.preventDefault();
        setHudVisible((visible) => !visible);
        lastTouchTapRef.current = null;
        return;
      }
    }

    lastTouchTapRef.current = {
      time: now,
      x: touch.clientX,
      y: touch.clientY,
    };
  };

  return (
    <div
      className="relative w-full h-screen bg-[#020403] overflow-hidden rmu-mfd-shell touch-manipulation"
      onTouchEnd={handleRootTouchEnd}
    >
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        onChange={handleFileUpload}
        className="hidden"
      />

      {/* Three.js container */}
      <div ref={containerRef} className="absolute inset-0" />

      {/* Website / Furuno-inspired multifunction display overlay. Pure CSS, no reference-image assets. */}
      {!captureMode && (
        <>
          <div className="pointer-events-none fixed inset-0 z-10 rmu-screen-vignette" />
          <div className="pointer-events-none fixed left-0 right-0 top-0 z-30 h-8 bg-[rgba(3,8,12,0.84)] border-b border-[rgba(122,255,91,0.34)] font-mono text-[10px] text-[#9cff78] flex items-center justify-between px-4 tracking-[0.18em]">
            <span>RED GLOVE PRODUCTIONS // REALMATHUNIVERSE</span>
            <span>OSC 9100 // WS 8787 // DATAFIELD ONLINE</span>
          </div>
          <div className="pointer-events-none fixed right-4 top-12 z-30 w-56 rounded-sm border border-[rgba(122,255,91,0.32)] bg-[rgba(0,0,0,0.42)] p-3 font-mono text-[10px] text-[#9cff78] shadow-[0_0_24px_rgba(122,255,91,0.08)]">
            <div className="mb-2 border-b border-[rgba(122,255,91,0.22)] pb-1 text-[11px] tracking-[0.22em] text-white">
              NAV / SIM
            </div>
            <div className="flex justify-between">
              <span>DATA</span>
              <span>
                {datasetStatus === "loaded"
                  ? "MERGED NAV"
                  : datasetStatus.toUpperCase()}
              </span>
            </div>
            <div className="flex justify-between">
              <span>PARTICLES</span>
              <span>{hudData.particleCount.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span>VCV</span>
              <span>{vcvModeLabel === "VCV LIVE" ? "LIVE" : "STANDBY"}</span>
            </div>
            <div className="mt-2 h-20 rounded-sm border border-[rgba(122,255,91,0.2)] rmu-radar-sweep" />
          </div>
          {!hudVisible && (
            <div className="pointer-events-none fixed left-1/2 top-12 z-40 -translate-x-1/2 rounded-sm border border-[rgba(122,255,91,0.24)] bg-[rgba(0,0,0,0.42)] px-3 py-1 font-mono text-[9px] tracking-[0.14em] text-[#9cff78] sm:hidden">
              DOUBLE TAP TO RESTORE HUD
            </div>
          )}
        </>
      )}

      {/* HUD */}
      {hudVisible && !captureMode && (
        <div
          className="fixed left-4 top-4 bottom-4 w-80 bg-[rgba(5,8,20,0.92)] border border-[rgba(0,200,255,0.3)] rounded p-3 font-mono text-[11px] text-[#00d4ff] overflow-y-auto z-50 select-none"
          style={{ boxShadow: "0 0 30px rgba(0, 200, 255, 0.1)" }}
        >
          {/* Header */}
          <div className="text-center mb-3 pb-2 border-b border-[rgba(0,200,255,0.3)]">
            <div className="text-[#00ff88] text-sm font-bold tracking-widest">
              REALMATHIVERSE
            </div>
            <div className="text-[rgba(0,200,255,0.6)] text-[9px] tracking-wider">
              TACTICAL RESEARCH CONSOLE
            </div>
          </div>

          {/* System */}
          <Section title="SYSTEM">
            <Row
              label="FPS"
              value={hudData.fps.toFixed(1)}
              status={
                hudData.fps >= 55 ? "ok" : hudData.fps >= 30 ? "warn" : "error"
              }
            />
            <Row
              label="Frame Time"
              value={`${hudData.frameTime.toFixed(2)}ms`}
              status={
                hudData.frameTime <= 16
                  ? "ok"
                  : hudData.frameTime <= 33
                    ? "warn"
                    : "error"
              }
            />
          </Section>

          {/* Dataset */}
          <Section title="DATASET">
            <Row
              label="Status"
              value={
                datasetStatus === "loaded"
                  ? "LOADED"
                  : datasetStatus === "loading"
                    ? "LOADING..."
                    : "SYNTHETIC"
              }
              status={
                datasetStatus === "loaded"
                  ? "ok"
                  : datasetStatus === "loading"
                    ? "active"
                    : "warn"
              }
            />
            <Row
              label="Particles"
              value={hudData.particleCount.toLocaleString()}
            />
            {datasetStatus === "loaded" && (
              <Row
                label="Data Rows"
                value={datasetRowCount.toLocaleString()}
                status="ok"
              />
            )}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="mt-2 w-full bg-[rgba(0,200,255,0.15)] border border-[rgba(0,200,255,0.4)] text-[#00d4ff] px-3 py-1.5 rounded text-[10px] hover:bg-[rgba(0,200,255,0.3)] transition-colors pointer-events-auto"
            >
              LOAD CSV FILE
            </button>
          </Section>

          {/* Runtime */}
          <Section title="RUNTIME">
            <Row
              label="State"
              value={hudData.paused ? "PAUSED" : "RUNNING"}
              status={hudData.paused ? "warn" : "active"}
            />
            <Row
              label="Time Scale"
              value={`${hudData.timeScale.toFixed(2)}x`}
              status={hudData.timeScale <= 0.3 ? "ok" : "warn"}
            />
          </Section>

          {/* Modulation */}
          <Section title="MODULATION">
            <Row
              label="VCV Status"
              value={
                vcvModeLabel === "VCV LIVE"
                  ? "VCV LIVE"
                  : vcvConnected
                    ? "BRIDGE ONLINE"
                    : vcvLoopStatus === "loaded"
                      ? "DB PLAYBACK"
                      : "DISCONNECTED"
              }
              status={
                vcvModeLabel === "VCV LIVE"
                  ? "ok"
                  : vcvConnected
                    ? "warn"
                    : vcvLoopStatus === "loaded"
                      ? "active"
                      : "error"
              }
            />
            <Row
              label="Mode"
              value={vcvLoopStatus === "loaded" && !vcvConnected ? "VCV DATABASE PLAYBACK" : vcvModeLabel}
              status={
                vcvModeLabel === "VCV LIVE"
                  ? "active"
                  : vcvConnected
                    ? "warn"
                    : vcvLoopStatus === "loaded"
                      ? "active"
                      : "error"
              }
            />
            <Row label="OSC In" value="127.0.0.1:9100" />
            <Row label="OSC Out" value="127.0.0.1:7001" />
            <Row label="WS Bridge" value="127.0.0.1:8787/vcv" />
            <Row
              label="Last Msg"
              value={
                vcvConnected && vcvLastMessageRef.current > 0
                  ? `${Math.round((Date.now() - vcvLastMessageRef.current) / 1000)}s ago`
                  : "N/A"
              }
            />
            <Row label="Last OSC" value={vcvLastAddress} />
            <Row label="Last Value" value={vcvLastValue} />
            <Row
              label="VCV DB Loop"
              value={
                vcvLoopStatus === "loaded"
                  ? "PLAYBACK"
                  : vcvLoopStatus.toUpperCase()
              }
              status={
                vcvLoopStatus === "loaded"
                  ? "active"
                  : vcvLoopStatus === "loading"
                    ? "warn"
                    : "error"
              }
            />
            {vcvLoopStatus === "loaded" && (
              <>
                <Row
                  label="VCV DB Events"
                  value={hudData.vcvLoopEventCount.toLocaleString()}
                  status="ok"
                />
                <Row
                  label="VCV DB Capture"
                  value={`${hudData.vcvLoopDurationSec.toFixed(1)}s`}
                />
                <Row
                  label="VCV DB Stretch"
                  value={`${vcvLoopScaleRef.current.toFixed(1)}x`}
                />
                <Row
                  label="VCV DB Last"
                  value={vcvLoopLastAppliedAddressRef.current}
                />
              </>
            )}
          </Section>

          {/* Behavior */}
          <Section title="BEHAVIOR">
            <Row
              label="Engine"
              value={hudData.behaviorEnabled ? "ON" : "OFF"}
              status={hudData.behaviorEnabled ? "ok" : "warn"}
            />
            <Row label="Mode" value={hudData.behaviorMode} status="active" />
            <Row
              label="Respawn"
              value={hudData.respawnEnabled ? "ON" : "OFF"}
            />
            <Row label="Captured" value={hudData.capturedCount.toString()} />
          </Section>

          {/* Gravity Well */}
          <Section title="GRAVITY WELL">
            <Row
              label="Strength"
              value={`${(hudData.gravityStrength * 100).toFixed(0)}%`}
            />
            <Bar value={hudData.gravityStrength} />
          </Section>

          {/* Footer */}
          <div className="text-center mt-2 text-[rgba(0,200,255,0.4)] text-[9px]">
            H HUD | mobile double-tap HUD | 0-7 modes | [ / ] sim speed | T reset speed | VCV DB loop =
            public playback
          </div>
        </div>
      )}

      {/* VCV Control Panel */}
      {vcvPanelVisible && !captureMode && (
        <VCVPanel
          modulation={modulation.current}
          onModulationChange={(key, value) => {
            const numericKey = key as
              | "radialField"
              | "orbitalField"
              | "verticalField"
              | "turbulenceField"
              | "probability";
            modulation.current[numericKey] = value;
            modulationTarget.current[numericKey] = value;
          }}
          gravityStrength={gravityStrengthRef.current}
          onGravityStrengthChange={(v) => {
            gravityStrengthRef.current = v;
            gravityStrengthTargetRef.current = v;
          }}
          onPresetSelect={(preset) => {
            switch (preset) {
              case "orbit":
                behaviorModeRef.current = BehaviorMode.STABLE_ORBIT;
                gravityStrengthRef.current = 0.3;
                break;
              case "blackhole":
                behaviorModeRef.current = BehaviorMode.BLACK_HOLE;
                gravityStrengthRef.current = 0.9;
                break;
              case "expansion":
                behaviorModeRef.current = BehaviorMode.EXPANSION;
                gravityStrengthRef.current = 0;
                break;
              case "turbulence":
                behaviorModeRef.current = BehaviorMode.TURBULENCE_BURST;
                break;
              case "collapse":
                behaviorModeRef.current = BehaviorMode.COLLAPSE;
                gravityStrengthRef.current = 1;
                break;
            }
          }}
        />
      )}

      {/* Capture mode indicator */}
      {captureMode && (
        <div className="fixed top-4 right-4 bg-[rgba(255,68,102,0.3)] border border-[rgba(255,68,102,0.6)] text-[#ff4466] px-4 py-2 rounded font-mono text-xs">
          CAPTURE MODE - Press C to exit
        </div>
      )}
    </div>
  );
}

// HUD Components
function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-3 pb-2 border-b border-[rgba(0,200,255,0.15)]">
      <div className="text-[#00ff88] text-[10px] font-semibold uppercase tracking-wider mb-1.5">
        {title}
      </div>
      {children}
    </div>
  );
}

function Row({
  label,
  value,
  status,
}: {
  label: string;
  value: string;
  status?: "ok" | "warn" | "error" | "active";
}) {
  const statusColors = {
    ok: "text-[#00ff88]",
    warn: "text-[#ffaa00]",
    error: "text-[#ff4466]",
    active: "text-[#00ffff]",
  };

  return (
    <div className="flex justify-between mb-0.5">
      <span className="text-[rgba(0,200,255,0.7)]">{label}</span>
      <span
        className={`font-medium ${status ? statusColors[status] : "text-[#00d4ff]"}`}
      >
        {value}
      </span>
    </div>
  );
}

function Bar({ value }: { value: number }) {
  return (
    <div className="h-1 bg-[rgba(0,100,150,0.3)] rounded overflow-hidden mt-1">
      <div
        className="h-full bg-gradient-to-r from-[#00d4ff] to-[#00ff88] rounded transition-all duration-100"
        style={{ width: `${Math.min(100, Math.max(0, value * 100))}%` }}
      />
    </div>
  );
}

// VCV Panel Component
interface VCVPanelProps {
  modulation: {
    radialField: number;
    orbitalField: number;
    verticalField: number;
    turbulenceField: number;
    probability: number;
  };
  onModulationChange: (key: string, value: number) => void;
  gravityStrength: number;
  onGravityStrengthChange: (value: number) => void;
  onPresetSelect: (preset: string) => void;
}

function VCVPanel({
  modulation,
  onModulationChange,
  gravityStrength,
  onGravityStrengthChange,
  onPresetSelect,
}: VCVPanelProps) {
  return (
    <div
      className="fixed right-[340px] top-4 bottom-4 w-72 bg-[rgba(20,15,30,0.95)] border border-[rgba(255,100,50,0.4)] rounded p-3 font-mono text-[10px] text-[#ff6644] overflow-y-auto z-40 select-none"
      style={{ boxShadow: "0 0 30px rgba(255, 100, 50, 0.1)" }}
    >
      {/* Header */}
      <div className="text-center mb-3 pb-2 border-b border-[rgba(255,100,50,0.3)]">
        <div className="text-[#ffaa00] text-xs font-bold tracking-widest">
          VCV BRIDGE
        </div>
        <div className="text-[rgba(255,100,50,0.6)] text-[9px]">
          SIMULATED CONTROL
        </div>
      </div>

      {/* Presets */}
      <VCVSection title="PRESETS">
        <div className="flex flex-wrap gap-1">
          {["orbit", "blackhole", "expansion", "turbulence", "collapse"].map(
            (p) => (
              <button
                key={p}
                onClick={() => onPresetSelect(p)}
                className="bg-[rgba(255,100,50,0.2)] border border-[rgba(255,100,50,0.4)] text-[#ff6644] px-2 py-1 rounded text-[10px] hover:bg-[rgba(255,100,50,0.4)] transition-colors pointer-events-auto"
              >
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ),
          )}
        </div>
      </VCVSection>

      {/* Field Controls */}
      <VCVSection title="FIELD CONTROLS">
        <Slider
          label="Radial Field"
          value={modulation.radialField}
          onChange={(v) => onModulationChange("radialField", v)}
        />
        <Slider
          label="Orbital Field"
          value={modulation.orbitalField}
          onChange={(v) => onModulationChange("orbitalField", v)}
        />
        <Slider
          label="Vertical Field"
          value={modulation.verticalField}
          onChange={(v) => onModulationChange("verticalField", v)}
        />
        <Slider
          label="Turbulence"
          value={modulation.turbulenceField}
          onChange={(v) => onModulationChange("turbulenceField", v)}
        />
      </VCVSection>

      {/* Gravity Well */}
      <VCVSection title="GRAVITY WELL">
        <Slider
          label="Strength"
          value={gravityStrength}
          onChange={onGravityStrengthChange}
        />
      </VCVSection>

      {/* Probability */}
      <VCVSection title="PROBABILITY">
        <Slider
          label="Probability"
          value={modulation.probability}
          onChange={(v) => onModulationChange("probability", v)}
        />
      </VCVSection>

      {/* Connection */}
      <VCVSection title="CONNECTION">
        <div className="text-[#ffaa00]">Simulated</div>
      </VCVSection>
    </div>
  );
}

function VCVSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-3 pb-2 border-b border-[rgba(255,100,50,0.2)]">
      <div className="text-[#ffaa00] text-[10px] font-semibold uppercase tracking-wider mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}

function Slider({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="mb-2 pointer-events-auto">
      <div className="flex justify-between mb-1">
        <span className="text-[rgba(255,100,50,0.8)]">{label}</span>
        <span className="text-[#ff6644] font-medium">{value.toFixed(2)}</span>
      </div>
      <input
        type="range"
        min="0"
        max="1"
        step="0.01"
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-1.5 appearance-none bg-[rgba(100,50,0,0.4)] rounded cursor-pointer
          [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5 
          [&::-webkit-slider-thumb]:bg-gradient-to-br [&::-webkit-slider-thumb]:from-[#ff6644] [&::-webkit-slider-thumb]:to-[#ffaa00] 
          [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-[0_0_6px_rgba(255,100,50,0.5)]"
      />
    </div>
  );
}
